/**
 * Canonical brand URL identity — Notion-style.
 *
 * The `[brandSlug]` URL segment carries the brand UUID as a trailing suffix,
 * optionally prefixed with the human-readable slug for readability:
 *
 *     /brands/adidas-b4428c13-8710-46eb-b402-83dc37c880e6/creatives
 *     /brands/b4428c13-8710-46eb-b402-83dc37c880e6            (empty brands)
 *
 * ONLY the trailing UUID resolves the brand — the prefix is pure decoration
 * (exactly like notion.so page URLs). Renaming a brand therefore never
 * invalidates a URL, never changes query keys, and never remounts the tree:
 * the UUID is the single stable identity everywhere.
 *
 * Legacy plain-slug URLs (old bookmarks, backend-generated links) contain no
 * UUID; they are resolved server-side by the middleware's slug path and
 * normalized to canonical form once by BrandContext.
 */

const TRAILING_UUID_RE = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

/**
 * Extract the brand UUID from a URL segment (`adidas-{uuid}`, bare `{uuid}`).
 * Returns null for legacy plain-slug segments.
 */
export function parseBrandUuidFromSegment(segment: string | undefined | null): string | null {
    if (!segment) return null
    const match = segment.match(TRAILING_UUID_RE)
    return match ? match[1].toLowerCase() : null
}

/**
 * Build the canonical URL segment for a brand. Empty brands get the bare UUID
 * (their placeholder slug is meaningless); real brands get the readable
 * `slug-{uuid}` form.
 */
export function buildBrandSegment(brand: { uuid: string; slug?: string | null; emptyBrand?: boolean }): string {
    const uuid = brand.uuid.toLowerCase()
    if (brand.emptyBrand || !brand.slug) return uuid
    return `${brand.slug}-${uuid}`
}

/**
 * Does an identifier (uuid, canonical `slug-{uuid}` segment, or plain slug)
 * refer to this brand? Use this for EVERY comparison between a brand object
 * and a client-held identifier — `b.slug === identifier` alone misses the
 * UUID identity that useBrandContext().brandSlug carries.
 */
export function brandMatchesIdentifier(
    brand: { uuid?: string | null; slug?: string | null },
    identifier: string | undefined | null
): boolean {
    if (!identifier) return false
    const uuid = parseBrandUuidFromSegment(identifier)
    if (uuid && brand.uuid && brand.uuid.toLowerCase() === uuid) return true
    return !!brand.slug && brand.slug === identifier
}
